import React from "react";
import { toast } from "sonner";
import { Lock, ArrowRight } from "@phosphor-icons/react";

const UNLOCK_PIN = "7427";
const STORAGE_KEY = "dw_unlocked_v1";

export function isUnlocked() {
  try { return localStorage.getItem(STORAGE_KEY) === "true"; } catch { return false; }
}

export function setUnlocked() {
  try { localStorage.setItem(STORAGE_KEY, "true"); } catch { /* noop */ }
}

export default function ComingSoon({ onUnlock }) {
  const [digits, setDigits] = React.useState(["", "", "", ""]);
  const [error, setError] = React.useState(false);
  const refs = [React.useRef(), React.useRef(), React.useRef(), React.useRef()];

  const focus = (i) => refs[i]?.current?.focus();

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = clean;
    setDigits(next);
    setError(false);
    if (clean && i < 3) focus(i + 1);
    if (next.every((d) => d.length === 1)) tryUnlock(next.join(""));
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) focus(i - 1);
    if (e.key === "Enter") tryUnlock(digits.join(""));
  };

  const onPaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (pasted.length === 4) {
      e.preventDefault();
      const next = pasted.split("");
      setDigits(next);
      tryUnlock(pasted);
    }
  };

  const tryUnlock = (code) => {
    if (code === UNLOCK_PIN) {
      setUnlocked();
      toast.success("Access granted");
      setTimeout(() => onUnlock?.(), 400);
    } else {
      setError(true);
      setDigits(["", "", "", ""]);
      focus(0);
      toast.error("Invalid PIN");
    }
  };

  React.useEffect(() => { focus(0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-70 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none noise" />

      <div className="relative min-h-screen grid place-items-center px-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-9 h-9 border border-primary grid place-items-center primary-glow">
              <span className="font-heading font-black text-primary text-sm leading-none">DW</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-sm">DW Mortgage Hub</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-mono">Mortgage · Lending · Advisory</div>
            </div>
          </div>

          <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-mono text-muted-foreground border border-white/10 px-3 py-1 rounded-sm">
            <span className="w-1 h-1 bg-primary rounded-full" /> Coming soon
          </span>

          <h1 className="mt-6 font-heading font-black text-5xl sm:text-6xl tracking-tighter leading-[0.92]">
            Almost <span className="text-primary">live.</span><br />
            <span className="italic font-medium">Enter to preview.</span>
          </h1>
          <p className="mt-5 text-sm text-muted-foreground max-w-sm">
            The DW Mortgage Hub public launch is a few days out. Have a 4-digit access PIN? Drop it below to preview the site.
          </p>

          <div className="mt-10">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Access PIN</label>
            <div className="flex gap-3" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={refs[i]}
                  data-testid={`pin-digit-${i}`}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  className={`w-16 h-16 text-center font-heading font-black text-3xl bg-transparent border rounded-sm outline-none transition-colors ${error ? "border-destructive text-destructive" : "border-white/15 focus:border-primary text-foreground"}`}
                  aria-label={`PIN digit ${i + 1}`}
                />
              ))}
            </div>
            {error && <div className="mt-3 text-xs text-destructive font-mono">Invalid PIN. Try again.</div>}
          </div>

          <button
            onClick={() => tryUnlock(digits.join(""))}
            data-testid="pin-submit"
            className="mt-8 inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-sm font-medium primary-glow hover-lift text-sm"
          >
            <Lock size={14} weight="fill" /> Unlock preview <ArrowRight size={14} />
          </button>

          <div className="mt-10 pt-6 border-t border-white/[0.06] flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <span>© 2026 DW Mortgage Hub</span>
            <span>Encrypted intake · NMLS #123456</span>
          </div>
        </div>
      </div>
    </div>
  );
}
